/* eslint-disable jira/i18n/no-export-define-message */
/* eslint-disable jira/choreographer/message-id-by-folder-structure */
import { useCallback, useEffect, useState, useRef } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { EMBEDDED_CONFLUENCE_MODE } from '@atlassian/embedded-confluence';
import type { EmbeddedContentModalProps } from '@atlassian/embedded-confluence/embedded-content-modal';
import type { OnEmbeddedContentAction } from '@atlassian/embedded-confluence/embedded-content-modal/types';
import { useEmbeddedConfluenceSidePanel } from '@jira/confluence__confluence-integration-controls/src/controllers/use-embedded-confluence-side-panel/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useFlagService, toFlagId } from '@atlassian/jira-flags';

import { useIntl, defineMessages } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import {
	LINK_PAGE_SUCCESS_FLAG_ID,
	PUBLISH_DRAFT_SUCCESS_FLAG_ID,
	LINK_PAGE_FAILED_FLAG_ID,
} from './PublishDraftSuccessFlagId.tsx';
import { convertToViewPageUrl } from './convertToViewPageUrl.tsx';
import { useEmbeddedPageTracking } from './useEmbeddedPageTracking.tsx';
import type { TrackEmbeddedConfluence } from './EmbeddedPageModalProps.types.tsx';

export const messages: {
	publishPageSuccessTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	publishPageSuccessDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	deferSpacePublishPageSuccessDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	publishPageFailedTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	publishPageFailedDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linkPageFailedTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linkPageFailedDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	publishAndLinkPageSuccessTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	publishAndLinkPageSuccessDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	publishAndLinkPageSuccessDescriptionIssueTermRefresh: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linkLiveDocSuccessTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linkLiveDocSuccessDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linkLiveDocSuccessDescriptionIssueTermRefresh: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	publishPageSuccessTitle: {
		id: 'issue-create-confluence-content.controllers.use-embedded-page-draft.publish-page-success-title',
		defaultMessage: 'Published!',
		description:
			'The title text on the notification displayed when a user successfully publishes a page',
	},
	publishPageSuccessDescription: {
		id: 'issue-create-confluence-content.controllers.use-embedded-page-draft.publish-page-success-description',
		defaultMessage: 'Successfully published page',
		description:
			'The description text on the notification displayed when a user successfully publishes a page',
	},
	deferSpacePublishPageSuccessDescription: {
		id: 'issue-create-confluence-content.controllers.use-embedded-page-draft.defer-space-publish-page-success-description',
		defaultMessage: 'Your page was successfully published in the {spaceName} space',
		description:
			'The description text on the notification displayed when a user successfully publishes a page',
	},
	publishPageFailedTitle: {
		id: 'issue-create-confluence-content.controllers.use-embedded-page-draft.publish-page-failed-title',
		defaultMessage: 'Something went wrong',
		description: 'The title text on the notification displayed when a user fails to publish a page',
	},
	publishPageFailedDescription: {
		id: 'issue-create-confluence-content.controllers.use-embedded-page-draft.publish-page-failed-description',
		defaultMessage: 'We are unable to publish your page at this time',
		description:
			'The description text on the notification displayed when a user fails to publish a page',
	},
	linkPageFailedTitle: {
		id: 'issue-create-confluence-content.controllers.use-embedded-page-draft.link-page-failed-title',
		defaultMessage: 'Something went wrong.',
		description: 'The title text on the notification displayed when a user fails to link a page',
	},
	linkPageFailedDescription: {
		id: 'issue-create-confluence-content.controllers.use-embedded-page-draft.link-page-failed-description',
		defaultMessage: 'We are unable to link your page at this time',
		description:
			'The description text on the notification displayed when a user fails to link a page',
	},
	publishAndLinkPageSuccessTitle: {
		id: 'issue-create-confluence-content.controllers.use-embedded-page-draft.publish-and-link-page-success-title',
		defaultMessage: 'Published!',
		description:
			'The title text on the notification displayed when a user successfully publishes and links a page',
	},
	publishAndLinkPageSuccessDescription: {
		id: 'issue-create-confluence-content.controllers.use-embedded-page-draft.publish-and-link-page-success-description',
		defaultMessage: 'Your page has been published and linked to your Jira issue.',
		description:
			'The description text on the notification displayed when a user publishes and successfully links a page',
	},
	publishAndLinkPageSuccessDescriptionIssueTermRefresh: {
		id: 'issue-create-confluence-content.controllers.use-embedded-page-draft.publish-and-link-page-success-description-issue-term-refresh',
		defaultMessage: 'Your page has been published and linked to your Jira work item.',
		description:
			'The description text on the notification displayed when a user publishes and successfully links a page',
	},
	linkLiveDocSuccessTitle: {
		id: 'issue-create-confluence-content.controllers.use-embedded-page-draft.link-live-doc-success-title',
		defaultMessage: 'Live doc created',
		description:
			'The title text on the notification displayed when a user successfully links a live doc',
	},
	linkLiveDocSuccessDescription: {
		id: 'issue-create-confluence-content.controllers.use-embedded-page-draft.link-live-doc-success-description',
		defaultMessage: 'A live doc was created and linked to your Jira issue.',
		description:
			'The description text on the notification displayed when a user successfully links a live doc',
	},
	linkLiveDocSuccessDescriptionIssueTermRefresh: {
		id: 'issue-create-confluence-content.controllers.use-embedded-page-draft.link-live-doc-success-description-issue-term-refresh',
		defaultMessage: 'A live doc was created and linked to your Jira work item.',
		description:
			'The description text on the notification displayed when a user successfully links a live doc',
	},
});

export const EVENT_SUCCESS = 'success';
export const OPERATIONAL_EVENT_TYPE = 'operational';

export const useEmbeddedPageDraft = ({
	isLive,
	embeddedConfluenceSource,
	onClose,
	url,
	onEmbeddedContentAction,
	linkConfluencePage,
}: {
	url: string;
	isLive?: boolean;
	embeddedConfluenceSource?: string;
	onEmbeddedContentAction?: OnEmbeddedContentAction;
	onClose: EmbeddedContentModalProps['onClose'];
	linkConfluencePage?: (link: string, analyticsEvent: UIAnalyticsEvent) => void;
}): {
	isContinueDraftOpen: boolean;
	openContinueDraftModal: () => void;
	closeContinueDraftModal: () => void;
	closeAll: () => void;
	handleEmbeddedContentAction: OnEmbeddedContentAction;
	fireAnalyticsEvent: TrackEmbeddedConfluence;
	epMode: EMBEDDED_CONFLUENCE_MODE;
} => {
	const [
		{
			panel: { isOpen },
			analyticsSource,
		},
		actions,
	] = useEmbeddedConfluenceSidePanel();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isContinueDraftOpen, setIsContinueDraftOpen] = useState(false);
	const [isPublished, setIsPublished] = useState(false);
	const [epMode, setEpMode] = useState<EMBEDDED_CONFLUENCE_MODE>(
		isLive ? EMBEDDED_CONFLUENCE_MODE.VIEW_MODE : EMBEDDED_CONFLUENCE_MODE.EDIT_MODE,
	);
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagService();
	// This hook runs in the layout-mounted panel, outside the issue/work-item
	// provider tree. Once the issue-context mirror is removed the analytics source
	// is empty here, so behind `remove-issue-context-mirror-on-wiv` we use the
	// source captured from the writer at open time so events like deleteDraftPage
	// keep reporting jiraIssueSurfaceArea.
	const fireAnalyticsEvent = useEmbeddedPageTracking(
		fg('remove-issue-context-mirror-on-wiv') ? analyticsSource : undefined,
	);
	const handledLiveDocUrls = useRef(new Set<string>());
	const havePublishedRef = useRef(false);

	const openContinueDraftModal = useCallback((): void => {
		if (!isPublished) {
			setIsContinueDraftOpen(true);
			fireAnalyticsEvent({
				action: 'clicked',
				eventName: isLive ? 'closeLiveDoc' : 'closeUnpublishedDraft',
				embeddedConfluenceSource,
			});
		} else {
			onClose && onClose();
		}
	}, [embeddedConfluenceSource, fireAnalyticsEvent, isLive, isPublished, onClose]);

	const closeContinueDraftModal = (): void => {
		setIsContinueDraftOpen(false);
	};
	const closeAll = (): void => {
		closeContinueDraftModal();
		onClose && onClose();
	};
	const handleEmbeddedContentAction: OnEmbeddedContentAction = useCallback(
		(action, name) => {
			const linkingSuccessDescriptionMessage = () => {
				if (fg('jira-issue-terminology-refresh-m3')) {
					return isLive
						? messages.linkLiveDocSuccessDescriptionIssueTermRefresh
						: messages.publishAndLinkPageSuccessDescriptionIssueTermRefresh;
				}
				return isLive
					? messages.linkLiveDocSuccessDescription
					: messages.publishAndLinkPageSuccessDescription;
			};
			if (name === 'edit-page/close') {
				if (isPublished) {
					setEpMode(EMBEDDED_CONFLUENCE_MODE.VIEW_MODE);
					if (isOpen) {
						actions.setConfluenceData({
							contentUrl: url,
							mode: EMBEDDED_CONFLUENCE_MODE.VIEW_MODE,
						});
					}
				} else {
					openContinueDraftModal();
				}
			}
			if (name === 'edit-page/load' && action === 'taskSuccess') {
				if (
					havePublishedRef.current &&
					expValEquals('cc_jira_cppp_publish_fix', 'isEnabled', true, false)
				) {
					havePublishedRef.current = false;
				} else if (epMode === EMBEDDED_CONFLUENCE_MODE.VIEW_MODE) {
					setEpMode(EMBEDDED_CONFLUENCE_MODE.EDIT_MODE);
					if (isOpen) {
						actions.setConfluenceData({
							contentUrl: url,
							mode: EMBEDDED_CONFLUENCE_MODE.EDIT_MODE,
						});
					}
				}
			}
			if (name === 'edit-page/publish' && action === 'taskSuccess') {
				if (expValEquals('cc_jira_cppp_publish_fix', 'isEnabled', true, false)) {
					havePublishedRef.current = true;
				}
				if (isOpen) {
					actions.setConfluenceData({
						contentUrl: url,
						isDraft: false,
						mode: EMBEDDED_CONFLUENCE_MODE.VIEW_MODE,
					});
				}

				fireAnalyticsEvent({
					action: EVENT_SUCCESS,
					eventName: isLive ? 'createLiveDoc' : 'publishDraftPage',
					embeddedConfluenceSource,
					eventType: OPERATIONAL_EVENT_TYPE,
				});

				const analyticsEvent = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				});

				if (isPublished && !isLive) {
					showFlag({
						key: toFlagId(PUBLISH_DRAFT_SUCCESS_FLAG_ID),
						type: 'success',
						title: formatMessage(messages.publishPageSuccessTitle),
						description: formatMessage(messages.publishPageSuccessDescription),
						messageId:
							'issue-create-confluence-content.show-flag.success.publish-draft-success-flag.use-embedded-page-draft',
						messageType: 'transactional',
					});
				} else {
					setIsPublished(true);
					if (isOpen) {
						actions.setConfluenceData({
							contentUrl: url,
							isDraft: false,
						});
					}
					// TODO this is a temporary solution, remove when https://hello.atlassian.net/jira/software/projects/CBT/boards/4465?selectedIssue=CBT-2895 is complete
					setTimeout(() => {
						try {
							if (linkConfluencePage) {
								// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
								linkConfluencePage(convertToViewPageUrl(url)!, analyticsEvent);
								fireAnalyticsEvent({
									action: EVENT_SUCCESS,
									eventName: isLive ? 'linkConfluenceLiveDoc' : 'linkConfluencePage',
									embeddedConfluenceSource,
									eventType: OPERATIONAL_EVENT_TYPE,
								});
								showFlag({
									key: toFlagId(LINK_PAGE_SUCCESS_FLAG_ID),
									type: 'success',
									title: formatMessage(
										isLive
											? messages.linkLiveDocSuccessTitle
											: messages.publishAndLinkPageSuccessTitle,
									),
									description: formatMessage(linkingSuccessDescriptionMessage()),
									messageId:
										'issue-create-confluence-content.show-flag.success.link-page-success-flag.use-embedded-page-draft',
									messageType: 'transactional',
								});
							}
							// oxlint-disable-next-line @typescript-eslint/no-explicit-any
						} catch (error: any) {
							fireErrorAnalytics({
								meta: {
									id: isLive ? 'linkConfluenceLiveDoc' : 'linkConfluencePage',
									teamName: 'confluence-better-together',
								},
								attributes: { embeddedConfluenceSource },
								error,
							});
							showFlag({
								key: toFlagId(LINK_PAGE_FAILED_FLAG_ID),
								type: 'error',
								title: formatMessage(messages.linkPageFailedTitle),
								description: formatMessage(messages.linkPageFailedDescription),
								messageId:
									'issue-create-confluence-content.show-flag.error.link-page-failed-flag.use-embedded-page-draft',
								messageType: 'transactional',
							});
						}
					}, 3500);
				}
			}
			if (name === 'edit-page/publish' && action === 'taskFail') {
				fireErrorAnalytics({
					meta: {
						id: isLive ? 'createLiveDoc' : 'publishDraftPage',
						teamName: 'confluence-better-together',
					},
					attributes: { embeddedConfluenceSource },
					error: new Error(`Error publishing draft page: {name: ${name}, action: ${action}}`),
				});
			}
			onEmbeddedContentAction && onEmbeddedContentAction(action, name);
		},
		[
			createAnalyticsEvent,
			embeddedConfluenceSource,
			epMode,
			fireAnalyticsEvent,
			formatMessage,
			isPublished,
			linkConfluencePage,
			onEmbeddedContentAction,
			openContinueDraftModal,
			showFlag,
			url,
			actions,
			isOpen,
			isLive,
		],
	);

	// This is a little weird, but to avoid duplicating the code above, if the page is live and state local state is !isPublished,
	// we simulate the action of publishing from inside the ep modal. This in turn displays the view page, link the page to the issue
	// and show the flag about the page being published and linked.
	useEffect(() => {
		if (
			isLive &&
			embeddedConfluenceSource?.endsWith('Panel') &&
			!handledLiveDocUrls.current.has(url)
		) {
			handledLiveDocUrls.current.add(url);
			if (handledLiveDocUrls.current.has(url)) {
				handleEmbeddedContentAction('taskSuccess', 'edit-page/publish');
			} else {
				handleEmbeddedContentAction('taskFail', 'edit-page/publish');
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLive, url]);

	// in the case that the user creates a live doc and then wants to create a page, we must ensure we open the draft for the page in edit mode
	useEffect(() => {
		setEpMode(isLive ? EMBEDDED_CONFLUENCE_MODE.VIEW_MODE : EMBEDDED_CONFLUENCE_MODE.EDIT_MODE);
	}, [isLive]);

	return {
		isContinueDraftOpen,
		openContinueDraftModal,
		closeContinueDraftModal,
		closeAll,
		handleEmbeddedContentAction,
		fireAnalyticsEvent,
		epMode,
	};
};
